((root) => {
  if (root.NelpandaFreeGiftPolicy) return;

  const campaign = 'multiple-goal';
  const linePrice = (item) => Number(item.final_line_price ?? item.line_price ?? item.original_line_price ?? 0);

  function validateThresholds(current, thresholds) {
    const shipping = Number(current.free_shipping_bar_shipping_threshold);
    if (!Number.isSafeInteger(shipping) || shipping < 0) throw new Error('free shipping threshold must be a non-negative integer');
    if (thresholds.some((value) => !Number.isSafeInteger(value) || value <= 0)) {
      throw new Error('promotion goal thresholds must be positive integers');
    }
    if (thresholds[0] <= shipping || thresholds[1] <= thresholds[0] || thresholds[2] <= thresholds[1]) {
      throw new Error(`thresholds must increase above the $${shipping} free-shipping goal`);
    }
  }

  function qualifyingTotal(cart) {
    const items = Array.isArray(cart.items) ? cart.items : [];
    const itemsSubtotal = Number(cart.items_subtotal_price ?? items.reduce((total, item) => total + linePrice(item), 0));
    if (itemsSubtotal <= 0) return 0;

    const campaignSubtotal = items
      .filter((item) => item.properties?._free_gift_campaign === campaign)
      .reduce((total, item) => total + linePrice(item), 0);
    return Math.round(Math.max(0, itemsSubtotal - campaignSubtotal) * Number(cart.total_price || 0) / itemsSubtotal);
  }

  const cartBasisAttribute = '_free_gift_cart_basis';

  async function recordCartBasis(cart, { hasGifts, preserve = false } = {}) {
    if (!cart) throw new Error('Cart is unavailable for gift qualification');
    const items = Array.isArray(cart.items) ? cart.items : [];
    hasGifts ??= items.some((item) => item.properties?._free_gift_campaign === campaign);
    let value = '';
    if (hasGifts) {
      const originalMinor = items
        .filter((item) => item.properties?._free_gift_campaign !== campaign)
        .reduce((total, item) => total + Number(item.original_line_price), 0);
      const qualifyingMinor = qualifyingTotal(cart);
      if (cart.currency !== 'USD' || !Number.isSafeInteger(originalMinor) || originalMinor <= 0
        || !Number.isSafeInteger(qualifyingMinor) || qualifyingMinor < 0 || qualifyingMinor > originalMinor) {
        throw new Error('Invalid cart gift qualification');
      }
      if (preserve) {
        const saved = JSON.parse(cart.attributes?.[cartBasisAttribute] || 'null');
        if (saved?.version !== 1 || saved.currency !== cart.currency || saved.originalMinor !== originalMinor
          || !Number.isSafeInteger(saved.qualifyingMinor) || saved.qualifyingMinor < 0 || saved.qualifyingMinor > originalMinor) {
          throw new Error('Cart gift qualification is missing or stale; refresh the cart');
        }
        return cart;
      }
      value = JSON.stringify({ version: 1, currency: cart.currency, originalMinor, qualifyingMinor });
    }
    if (String(cart.attributes?.[cartBasisAttribute] || '') === value) return cart;
    const response = await root.fetch(`${root.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [cartBasisAttribute]: value } }),
    });
    if (!response.ok) throw new Error('Unable to record cart gift qualification');
    const saved = await response.json();
    if (String(saved.attributes?.[cartBasisAttribute] || '') !== value
      || saved.total_price !== cart.total_price
      || JSON.stringify(saved.items?.map((item) => [item.key, item.quantity, item.original_line_price]))
        !== JSON.stringify(items.map((item) => [item.key, item.quantity, item.original_line_price]))) {
      throw new Error('Cart changed while recording gift qualification; retry checkout');
    }
    return saved;
  }

  function validateCart(cart, goals, { upgradeEnabled = false } = {}) {
    const items = Array.isArray(cart.items) ? cart.items : [];
    const activeGoals = goals.filter((goal) => goal.enabled !== false);
    const goalsByNumber = new Map(activeGoals.map((goal) => [goal.goal, goal]));
    const goalsByVariant = new Map();
    activeGoals.forEach((goal) => {
      goalsByVariant.set(Number(goal.variantId), goal.goal);
      (goal.variants || []).forEach((variant) => goalsByVariant.set(Number(variant.id), goal.goal));
    });

    const amount = qualifyingTotal(cart);
    const eligibleGoals = activeGoals.filter((goal) => amount >= goal.thresholdCents);
    const eligibleGoalNumbers = new Set(eligibleGoals.map((goal) => goal.goal));
    const keptGoalNumbers = new Set();
    const invalidLines = items.filter((item) => {
      const markedGoal = item.properties?._free_gift_campaign === campaign
        ? Number(item.properties?._free_gift_goal)
        : goalsByVariant.get(Number(item.variant_id));
      if (!markedGoal) return false;

      const goal = goalsByNumber.get(markedGoal);
      const defaultVariantId = Number(item.properties?._free_gift_default_variant_id || item.variant_id);
      const selectedVariant = goal?.variants?.find((variant) => Number(variant.id) === Number(item.variant_id));
      const isPaidUpgrade = Boolean(item.properties?._free_gift_upgrade);
      const allowedAutoVariant = selectedVariant
        ? selectedVariant.allowed === true && selectedVariant.auto !== false
        : (!goal?.variants?.length || Number(item.variant_id) === goal?.variantId);
      const allowedUpgradeVariant = Boolean(
        upgradeEnabled
        && isPaidUpgrade
        && selectedVariant?.allowed === true
        && selectedVariant?.upgrade === true,
      );
      const valid = goal
        && eligibleGoalNumbers.has(markedGoal)
        && !keptGoalNumbers.has(markedGoal)
        && defaultVariantId === goal.variantId
        && item.quantity === 1
        && item.properties?._free_gift_campaign === campaign
        && Number(item.properties?._free_gift_goal) === markedGoal
        && (isPaidUpgrade ? allowedUpgradeVariant : allowedAutoVariant);

      if (valid) keptGoalNumbers.add(markedGoal);
      return !valid;
    });

    return {
      qualifyingTotal: amount,
      eligibleGoals,
      invalidLines,
      missingGoals: eligibleGoals.filter(({ goal }) => !keptGoalNumbers.has(goal)),
    };
  }

  root.NelpandaFreeGiftPolicy = Object.freeze({ campaign, qualifyingTotal, recordCartBasis, validateCart, validateThresholds });
})(typeof window === 'object' ? window : globalThis);
